// © Microsoft Corporation. All rights reserved.

import { getContextData, headData, RequestContext } from "@msnews/core";
import { OneServiceMetadataType } from "@msnews/river-data-types";
import { PublisherCardData, PublisherCarouselData, PublisherContentData, PublisherInfo } from "./PublisherCarouselData";
import { mapContentTypeTelemetryContentType } from "./PublisherCarousel.telemetry";
import { PublisherCarouselStrings } from "./PublisherCarousel.strings";
import { appErrorLogger } from "@msnews/social-utils";
import { AppError, PublisherCarouselDataMapperError } from "@msnews/diagnostics";
import { ImageFormat, generateImageUrlFromBaseUrl } from "@msnews/web-worker-shared-utils";
import { String } from "typescript-string-operations";
import { TelemetryObject } from "@msnews/telemetry-core";
import Globe from "@msnews/icons-wc/icons/Globe.svg";
import GalleryIndicator from "@msnews/icons-wc/icons/GalleryIndicator.svg";
import VideoIndicator from "@msnews/icons-wc/icons/VideoIndicator.svg";

const publisherProfilePageUrl = (profileId: string, locale: string): string => {
    return `https://www.msn.com/${locale}/community/channel/${profileId}`;
};

export function mapPublisherContentToCarouselData(
    publisherContentDataArray: PublisherContentData[],
    imageSize: {width: number, height: number},
    localizedStrings: PublisherCarouselStrings
): PublisherCarouselData {
    const publishersMap = {};

    try {
        const cards: PublisherCardData[] = publisherContentDataArray.map((publisherContentData, index) => {
            publishersMap[publisherContentData.provider?.name] = publisherContentData.provider;

            return mapContentToCardData(publisherContentData, imageSize);
        });

        const publishers: PublisherInfo[] = Object.values(publishersMap);
        const carouselData: PublisherCarouselData = {
            cards,
            publishers,
            seeAllLinkUrl: mapDestinationHref(publisherProfilePageUrl(publishers[0]?.profileId, "en-us")),
            title: String.Format(localizedStrings.morePublisherContentTitle, { publisherName: publishers[0]?.name?.toUpperCase() || ""})
        };

        return carouselData;
    } catch (err) {
        appErrorLogger.log({
            ...PublisherCarouselDataMapperError,
            message: "PublisherCarousel experience data mapper failed to process the response",
            pb: {
                ...PublisherCarouselDataMapperError.pb,
                stack: err.stack,
                publisherContentDataArray
            }
        } as AppError);
    }
}

export function mapContentToCardData(contentData: PublisherContentData, imageSize: {width: number, height: number}) {
    const image = contentData.images[0];
    const card: PublisherCardData = {
        clickUrl: mapDestinationHref(contentData.url),
        contentIndicator: mapContentTypeIndicatorIcon(contentData.type as OneServiceMetadataType),
        id: `${"publisher-carousel-card"}-${contentData.id}`,
        imageInfo: image ? {
            altText: contentData.title,
            sourceImageUrl: generateImageUrlFromBaseUrl(
                image.url, {
                    width: imageSize.width,
                    height: imageSize.height,
                    quality: 60,
                    mode: 6,
                    enableDpiScaling: false,
                    format: ImageFormat.JPG
                }
            )
        } : null,
        isPremium: contentData.subscriptionProductType?.toLowerCase() == "premium",
        publisher: {
            id: contentData.provider?.id,
            name: contentData.provider?.name,
            logoUrl: contentData.provider?.logoUrl ? `${contentData.provider?.logoUrl}?w=16&h=16` : "",
            profileId: contentData.provider?.profileId,
            isSubscribed: false // TODO
        },
        publishedDateTime: contentData.publishedDateTime,
        title: contentData.title || "",
        telemetry: {
            item: new TelemetryObject({
                name: "SubscriptionsUpsellCard",
                content: {
                    headline: contentData.title,
                    id: contentData.id,
                    category: contentData.category,
                    vertical: contentData.vertical,
                    type: mapContentTypeTelemetryContentType(contentData.type as OneServiceMetadataType)
                }
            }),
            destination: new TelemetryObject({
                name: "destination",
                content: {
                    headline: contentData.title,
                    id: contentData.id,
                    category: contentData.category,
                    vertical: contentData.vertical,
                    type: mapContentTypeTelemetryContentType(contentData.type as OneServiceMetadataType)
                }
            })
        }
    };

    return card;
}

function mapDestinationHref(contentUrl: string) {
    let qparams = "";
    if (headData && headData.ClientSettings && headData.ClientSettings.forwardablequeryparams) {
        qparams = `?${headData.ClientSettings.forwardablequeryparams}`;
    }
    return `${contentUrl}${qparams}`;
}

/**
 * Map content type icon based on type
 * @param type - content type : slideshow, video and web content
 * @return icon SVG string
 */
export function mapContentTypeIndicatorIcon(type?: OneServiceMetadataType): string {
    if (type === OneServiceMetadataType.Slideshow) {
        return GalleryIndicator;
    }

    if (type === OneServiceMetadataType.Video) {
        return VideoIndicator;
    }

    if (type === OneServiceMetadataType.WebContent) {
        return Globe;
    }
}

/**
 * @return combined string for publisher name and published date
 */
export function generatePublisherString(providerName: string, publishedDate: string) {
    return `${providerName || ""}${providerName && publishedDate &&  " · "}${publishedDate || ""}`;
}

/**
 * Performs geofence check based on users geo-location
 * @return boolean
 */
export function isStateAllowed() {
    // geofence check
    const allowedStates = ["alabama", "alaska", "california", "colorado", "connecticut", "delaware", "georgia", "idaho", "kansas", "massachusetts", "montana", "new hampshire", "north dakota", "oregon", "rhode island", "tennessee", "utah", "vermont", "virginia", "washington", "west virginia"];
    const isUserStateAllowed = headData?.ClientSettings && headData.ClientSettings["geo_country"]?.toLowerCase() == "us" && allowedStates.includes(headData.ClientSettings["geo_subdivision"]?.toLowerCase());

    if (!isUserStateAllowed) {
        // bypass geofence check with a special URL parameter
        const rc = new RequestContext();
        if (getContextData(rc.data?.locationHref, "locationHref").includes("bypassgeo")) { return true; }
    }

    return isUserStateAllowed;
}